"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getCookie, setCookie } from "@/lib/cookies";
import Link from "next/link";

type CookieCategory = {
    id: "analytics" | "marketing";
    title: string;
    description: string;
    checked: boolean;
};

const initialCategories: CookieCategory[] = [
    {
        id: "analytics",
        title: "Analytics",
        description:
            "Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc.",
        checked: true,
    },
    {
        id: "marketing",
        title: "Marketing",
        description:
            "Marketing cookies are used to provide visitors with customised advertisements based on the pages you visited previously and to analyse the effectiveness of the ad campaigns.",
        checked: true,
    },
];

export function CookieConsent({ onSave }: { onSave?: () => void }) {
    const [categories, setCategories] = useState<CookieCategory[]>(initialCategories);
    const [showDetails, setShowDetails] = useState(true);

    useEffect(() => {
        const analyticsConsent = getCookie("analytics_consent");
        const marketingConsent = getCookie("marketing_consent");

        setCategories(prevCategories => prevCategories.map(category => {
            if (category.id === 'analytics') {
                return { ...category, checked: analyticsConsent !== 'false' };
            }
            if (category.id === 'marketing') {
                return { ...category, checked: marketingConsent !== 'false' };
            }
            return category;
        }));
    }, []);

    const handleSwitchChange = (id: CookieCategory["id"]) => {
        setCategories(
            categories.map((category) =>
                category.id === id ? { ...category, checked: !category.checked } : category
            )
        );
    };

    const handleRejectAll = () => {
        setCategories(categories.map(category => ({ ...category, checked: false })));
        setCookie("analytics_consent", "false", 365);
        setCookie("marketing_consent", "false", 365);
        onSave?.();
    };

    const handleAcceptAll = () => {
        setCategories(categories.map(category => ({ ...category, checked: true })));
        setCookie("analytics_consent", "true", 365);
        setCookie("marketing_consent", "true", 365);
        onSave?.();
    };

    const handleSavePreferences = () => {
        const analyticsConsent = categories.find(c => c.id === 'analytics')?.checked;
        const marketingConsent = categories.find(c => c.id === 'marketing')?.checked;
        setCookie("analytics_consent", analyticsConsent ? "true" : "false", 365);
        setCookie("marketing_consent", marketingConsent ? "true" : "false", 365);
        onSave?.();
    };

    return (
        <div>
            <div>
                <p className="text-sm text-gray-600 mb-4">
                    We use cookies to help you navigate efficiently and perform certain functions. You will find detailed information about all cookies under each consent category below.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                    The cookies that are categorised as "Necessary" are stored on your browser as they are essential for enabling the basic functionalities of the site.
                </p>
                {showDetails && (
                    <p className="text-sm text-gray-600 mb-4">
                        We also use third-party cookies that help us analyse how you use this website, store your preferences, and provide the content and advertisements that are relevant to you. These cookies will only be stored in your browser with your prior consent.
                        <br /><br />
                        You can choose to enable or disable some or all of these cookies but disabling some of them may affect your browsing experience. <Link href="/legal/cookie-policy" className="underline">Cookie policy</Link>
                    </p>
                )}
                <button onClick={() => setShowDetails(!showDetails)} className="text-sm text-blue-600 hover:underline mb-4">
                    {showDetails ? "Show less" : "Show more"}
                </button>
            </div>

            <div className="space-y-4">
                <div>
                    <div className="w-full flex justify-between items-center">
                        <div className="font-semibold">
                            Necessary
                        </div>
                        <span className="text-sm font-semibold text-green-800">Always Active</span>
                    </div>
                    <div className="pt-2">
                        <p className="text-sm text-gray-600">
                            Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.
                        </p>
                    </div>
                </div>

                {categories.map((category) => (
                    <div key={category.id}>
                        <div className="w-full flex justify-between items-center">
                            <div className="font-semibold">
                                {category.title}
                            </div>
                            <Switch
                                checked={category.checked}
                                onCheckedChange={() => handleSwitchChange(category.id)}
                                aria-label={category.title}
                            />
                        </div>
                        <div className="pt-2">
                            <p className="text-sm text-gray-600">{category.description}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2 bg-gray-100 p-4 rounded-lg sm:flex-row sm:justify-between">
                <Button variant="ghost" onClick={handleRejectAll} className="w-full sm:w-auto">Reject All</Button>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    <Button variant="secondary" onClick={handleSavePreferences}>Save My Preferences</Button>
                    <Button variant="default" onClick={handleAcceptAll}>Accept All</Button>
                </div>
            </div>
        </div>
    );
}
